(function () {
  'use strict';

  // Guard: gulp bundles ALL assets/js/*.js into source.js, so this IIFE would run
  // twice on the rulehouse page — once from source.js (sync) and once from the
  // standalone <script defer> tag.  The first invocation wins; the second exits.
  if (window.__rhBooted) return;
  window.__rhBooted = true;

  // ─── Tweakables (button / wave — identical to links.js) ──────────────────────
  const BUTTON_ROW_OFFSET   = 92;   // 132 − 40: logo moved down 40px, offset reduced to keep buttons in place
  const MOBILE_ROW_OFFSET   = 30;   // 70 − 40: same compensation on mobile
  const PREVIEW_ABOVE_ROW   = -16;
  const WAVE_AMP_PX         = 10;
  const WAVE_SPEED          = 0.002;
  const WAVE_PHASE_STEP     = 0.8;
  const WAVE_SNAP_PX        = 4;
  const MOBILE_WAVE_X_AMP   = 64;
  const MOBILE_WAVE_X_SPEED = 0.0015;
  const MOBILE_WAVE_X_PHASE = 1.2;
  const MOBILE_WAVE_X_SNAP  = 2;
  const MQ_MOBILE           = '(max-width: 1030px)';

  // ─── Shader pixel scale ───────────────────────────────────────────────────────
  // x4 on desktop (>= 1030px), x2 on mobile (<1030px), x1 on tiny (<490px).
  // Computed dynamically in resize() so it responds to window resizes.
  const SCALE_DESKTOP   = 4;
  const SCALE_MOBILE    = 2;
  const SCALE_X1        = 1;
  const SCALE_BREAKPT   = 1030;
  const SCALE_BREAKPT_X1 = 490;

  // ─── GLSL — vertex ───────────────────────────────────────────────────────────
  const VERT_SRC = `#version 300 es
in vec2 a_position;
void main() { gl_Position = vec4(a_position, 0.0, 1.0); }`;

  // ─── GLSL — fragment (converted from Unity HLSL StarryBackground) ────────────
  const FRAG_SRC = `#version 300 es
precision highp float;

uniform float u_time;
out vec4 fragColor;

// ── Parameters (from Unity inspector) ────────────────────────────────────────
const vec3  BG_COLOR      = vec3(0.0,  0.0,  0.0);
const vec3  NEBULA_A      = vec3(0.192, 0.376, 0.510);  // #316082
const vec3  NEBULA_B      = vec3(0.267, 0.149, 0.239);  // #44263D
const float NEBULA_STR    = 0.824;
const float NEBULA_SCALE  = 108.0;
const vec2  NEBULA_SPEED  = vec2(1.0,  0.0);
const float NEBULA_DITHER = 0.15;    // Bayer pre-dither swing before palette snap

const float FAR_DENSITY   = 0.158;
const float FAR_CELL      = 5.0;
const vec2  FAR_SPEED     = vec2(0.2,  0.0);

const float MID_DENSITY   = 0.122;
const float MID_CELL      = 12.0;
const float MID_SPIKE     = 2.0;
const vec2  MID_SPEED     = vec2(0.4,  0.0);

const float NEAR_DENSITY  = 0.0112;
const float NEAR_CELL     = 10.24;
const float NEAR_SPIKE    = 7.0;
const vec2  NEAR_SPEED    = vec2(0.6,  0.0);

const float GIANT_DENSITY = 0.0065;
const float GIANT_CELL    = 21.94;
const float GIANT_SPIKE   = 22.5;
const vec2  GIANT_SPEED   = vec2(0.15, 0.0);

// Pixels-per-world-unit from the original Unity asset (controls scroll speed).
const float PPU = 16.0;

// ── Hash ──────────────────────────────────────────────────────────────────────
float Hash(vec2 p) {
    p  = fract(p * vec2(127.1, 311.7));
    p += dot(p, p + 74.3);
    return fract(p.x * p.y);
}

// ── Nebula ────────────────────────────────────────────────────────────────────
float SmoothNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(Hash(i),                 Hash(i + vec2(1.0, 0.0)), u.x),
        mix(Hash(i + vec2(0.0, 1.0)), Hash(i + vec2(1.0, 1.0)), u.x),
        u.y);
}

float NebulaVal(vec2 px, float scale) {
    vec2  p = px / scale;
    float n = SmoothNoise(p)             * 0.50;
         n += SmoothNoise(p * 2.1 + 3.7) * 0.30;
         n += SmoothNoise(p * 4.3 + 11.3)* 0.20;
    return n;
}

// ── Bayer 16×16 ordered dither ───────────────────────────────────────────────
// 256 threshold levels — near-smooth gradients between any two palette colours.
// Each row of the array corresponds to one row of the matrix (16 entries wide).
float bayer16(ivec2 pos) {
    const int B[256] = int[](
          0, 128,  32, 160,   8, 136,  40, 168,   2, 130,  34, 162,  10, 138,  42, 170,
        192,  64, 224,  96, 200,  72, 232, 104, 194,  66, 226,  98, 202,  74, 234, 106,
         48, 176,  16, 144,  56, 184,  24, 152,  50, 178,  18, 146,  58, 186,  26, 154,
        240, 112, 208,  80, 248, 120, 216,  88, 242, 114, 210,  82, 250, 122, 218,  90,
         12, 140,  44, 172,   4, 132,  36, 164,  14, 142,  46, 174,   6, 134,  38, 166,
        204,  76, 236, 108, 196,  68, 228, 100, 206,  78, 238, 110, 198,  70, 230, 102,
         60, 188,  28, 156,  52, 180,  20, 148,  62, 190,  30, 158,  54, 182,  22, 150,
        252, 124, 220,  92, 244, 116, 212,  84, 254, 126, 222,  94, 246, 118, 214,  86,
          3, 131,  35, 163,  11, 139,  43, 171,   1, 129,  33, 161,   9, 137,  41, 169,
        195,  67, 227,  99, 203,  75, 235, 107, 193,  65, 225,  97, 201,  73, 233, 105,
         51, 179,  19, 147,  59, 187,  27, 155,  49, 177,  17, 145,  57, 185,  25, 153,
        243, 115, 211,  83, 251, 123, 219,  91, 241, 113, 209,  81, 249, 121, 217,  89,
         15, 143,  47, 175,   7, 135,  39, 167,  13, 141,  45, 173,   5, 133,  37, 165,
        207,  79, 239, 111, 199,  71, 231, 103, 205,  77, 237, 109, 197,  69, 229, 101,
         63, 191,  31, 159,  55, 183,  23, 151,  61, 189,  29, 157,  53, 181,  21, 149,
        255, 127, 223,  95, 247, 119, 215,  87, 253, 125, 221,  93, 245, 117, 213,  85
    );
    return float(B[(pos.x & 15) + (pos.y & 15) * 16]) * (1.0 / 256.0);
}

// ── dawnBreaker32 palette snap ────────────────────────────────────────────────
// Nearest colour in the 32-entry palette, using squared Euclidean RGB distance.
// Colours read from dawnBreaker32.png (32×1); hardcoded for GPU-side speed.
vec3 palettize(vec3 col) {
    const vec3 PAL[32] = vec3[32](
        vec3(0.000, 0.000, 0.000),  // #000000
        vec3(0.133, 0.125, 0.204),  // #222034
        vec3(0.271, 0.157, 0.235),  // #45283C
        vec3(0.400, 0.224, 0.192),  // #663931
        vec3(0.561, 0.337, 0.231),  // #8F563B
        vec3(0.875, 0.443, 0.149),  // #DF7126
        vec3(0.851, 0.627, 0.400),  // #D9A066
        vec3(0.933, 0.765, 0.604),  // #EEC39A
        vec3(0.984, 0.949, 0.212),  // #FBF236
        vec3(0.600, 0.898, 0.314),  // #99E550
        vec3(0.416, 0.745, 0.188),  // #6ABE30
        vec3(0.216, 0.580, 0.431),  // #37946E
        vec3(0.294, 0.412, 0.184),  // #4B692F
        vec3(0.322, 0.294, 0.141),  // #524B24
        vec3(0.196, 0.235, 0.224),  // #323C39
        vec3(0.247, 0.247, 0.455),  // #3F3F74
        vec3(0.188, 0.376, 0.510),  // #306082  ← ≈ NEBULA_A
        vec3(0.357, 0.431, 0.882),  // #5B6EE1
        vec3(0.388, 0.608, 1.000),  // #639BFF
        vec3(0.373, 0.804, 0.894),  // #5FCDE4
        vec3(0.796, 0.859, 0.988),  // #CBDBFC
        vec3(1.000, 1.000, 1.000),  // #FFFFFF
        vec3(0.608, 0.678, 0.718),  // #9BADB7
        vec3(0.518, 0.494, 0.529),  // #847E87
        vec3(0.412, 0.416, 0.416),  // #696A6A
        vec3(0.349, 0.337, 0.322),  // #595652
        vec3(0.463, 0.259, 0.541),  // #76428A
        vec3(0.675, 0.196, 0.196),  // #AC3232
        vec3(0.851, 0.341, 0.388),  // #D95763
        vec3(0.843, 0.482, 0.729),  // #D77BBA
        vec3(0.561, 0.592, 0.290),  // #8F974A
        vec3(0.541, 0.435, 0.188)   // #8A6F30
    );
    vec3  best  = PAL[0];
    float bestD = dot(col - PAL[0], col - PAL[0]);
    for (int i = 1; i < 32; i++) {
        vec3  diff = col - PAL[i];
        float d    = dot(diff, diff);
        if (d < bestD) { bestD = d; best = PAL[i]; }
    }
    return best;
}

// ── Star helpers ──────────────────────────────────────────────────────────────
vec2 ScrollOff(vec2 speed) {
    return floor(speed * u_time * PPU);
}

bool GetStar(vec2 cell, float cellSize, float density, float seed, out vec2 starPx) {
    float rng = Hash(cell * 0.1 + seed);
    if (rng >= density) { starPx = vec2(0.0); return false; }
    float lx = floor(Hash(cell + seed + vec2(3.7,  0.0)) * cellSize);
    float ly = floor(Hash(cell + seed + vec2(0.0, 11.3)) * cellSize);
    starPx = cell * cellSize + vec2(lx, ly);
    return true;
}

vec3 StarColor(float rng) {
    if (rng < 0.18) return vec3(0.72, 0.87, 1.00); // blue-white
    if (rng < 0.55) return vec3(1.00, 1.00, 1.00); // white
    if (rng < 0.80) return vec3(1.00, 0.91, 0.72); // yellow
    return             vec3(1.00, 0.52, 0.22);      // orange
}

vec3 SpikeLight(vec2 px, vec2 sp, float spikeLen, vec3 col) {
    vec2  d   = abs(px - sp);
    float inv = 3.2 / max(spikeLen, 1.0);
    if (d.y < 0.5 && d.x > 0.5 && d.x <= spikeLen + 0.5)
        return col * exp(-d.x * inv);
    if (d.x < 0.5 && d.y > 0.5 && d.y <= spikeLen + 0.5)
        return col * exp(-d.y * inv);
    float diagLen = spikeLen * 0.55;
    if (abs(d.x - d.y) < 0.5 && d.x > 0.5 && d.x <= diagLen + 0.5)
        return col * 0.45 * exp(-d.x * 1.41 * inv);
    return vec3(0.0);
}

// ── Main ──────────────────────────────────────────────────────────────────────
void main() {
    // Canvas is rendered at 1/PIXEL_SCALE resolution — gl_FragCoord.xy are
    // already in game-pixel coordinates, no division needed.
    vec2 basePx = floor(gl_FragCoord.xy);

    vec2 pxNeb   = basePx - ScrollOff(NEBULA_SPEED);
    vec2 pxFar   = basePx - ScrollOff(FAR_SPEED);
    vec2 pxMid   = basePx - ScrollOff(MID_SPEED);
    vec2 pxNear  = basePx - ScrollOff(NEAR_SPEED);
    vec2 pxGiant = basePx - ScrollOff(GIANT_SPEED);

    // ── Nebula layer ──────────────────────────────────────────────────────────
    float nA     = NebulaVal(pxNeb, NEBULA_SCALE);
    float nB     = NebulaVal(pxNeb + vec2(200.0, 137.0), NEBULA_SCALE * 0.7);
    float nMask  = smoothstep(0.28, 0.72, nA);
    vec3  nebRgb = mix(NEBULA_A, NEBULA_B, smoothstep(0.35, 0.80, nB));
    vec3  nebula = BG_COLOR + nebRgb * nMask * NEBULA_STR;
    // Bayer 4×4 pre-dither then snap to dawnBreaker32 — stars added after, unpalettized
    float d    = bayer16(ivec2(gl_FragCoord.xy)) - 0.5;
    nebula     = palettize(clamp(nebula + d * NEBULA_DITHER, 0.0, 1.0));

    // ── Star accumulation ─────────────────────────────────────────────────────
    vec3 stars = vec3(0.0);

    // Far stars — single pixel, no spike
    {
        vec2 cell = floor(pxFar / FAR_CELL);
        vec2 sp;
        bool hit = GetStar(cell, FAR_CELL, FAR_DENSITY, 0.0, sp);
        if (hit && abs(pxFar.x - sp.x) < 0.5 && abs(pxFar.y - sp.y) < 0.5)
            stars = max(stars, StarColor(Hash(cell + vec2(77.7, 33.3)))
                             * mix(0.4, 0.85, Hash(cell + vec2(5.5, 9.9))));
    }

    // Mid stars — short cross spikes
    {
        vec2 basecell = floor(pxMid / MID_CELL);
        for (int dy = -2; dy <= 2; dy++)
        for (int dx = -2; dx <= 2; dx++) {
            vec2 cell = basecell + vec2(float(dx), float(dy));
            vec2 sp;
            bool hit = GetStar(cell, MID_CELL, MID_DENSITY, 17.0, sp);
            if (!hit) continue;
            vec3 sc = StarColor(Hash(cell + vec2(44.4, 22.2)))
                      * mix(0.7, 1.1, Hash(cell + vec2(1.1, 2.2)));
            if (abs(pxMid.x - sp.x) < 0.5 && abs(pxMid.y - sp.y) < 0.5)
                stars = max(stars, sc);
            else
                stars = max(stars, SpikeLight(pxMid, sp, MID_SPIKE, sc));
        }
    }

    // Bright stars — longer spikes
    {
        vec2 basecell = floor(pxNear / NEAR_CELL);
        for (int dy = -2; dy <= 2; dy++)
        for (int dx = -2; dx <= 2; dx++) {
            vec2 cell = basecell + vec2(float(dx), float(dy));
            vec2 sp;
            bool hit = GetStar(cell, NEAR_CELL, NEAR_DENSITY, 53.0, sp);
            if (!hit) continue;
            vec3 sc = StarColor(Hash(cell + vec2(88.1, 55.5)))
                      * mix(1.2, 1.8, Hash(cell + vec2(3.3, 7.7)));
            if (abs(pxNear.x - sp.x) < 0.5 && abs(pxNear.y - sp.y) < 0.5)
                stars = max(stars, sc);
            else
                stars = max(stars, SpikeLight(pxNear, sp, NEAR_SPIKE, sc));
        }
    }

    // Giant stars — prominent cross + diagonal spikes
    {
        vec2 basecell = floor(pxGiant / GIANT_CELL);
        for (int dy = -2; dy <= 2; dy++)
        for (int dx = -2; dx <= 2; dx++) {
            vec2  cell = basecell + vec2(float(dx), float(dy));
            float rng  = Hash(cell + vec2(12.3, 45.6));
            vec3  sc   = (rng < 0.5 ? vec3(0.80, 0.92, 1.00) : vec3(1.00, 1.00, 1.00))
                         * mix(2.2, 3.5, Hash(cell + vec2(6.6, 1.1)));
            vec2 sp;
            bool hit = GetStar(cell, GIANT_CELL, GIANT_DENSITY, 91.0, sp);
            if (!hit) continue;
            if (abs(pxGiant.x - sp.x) < 0.5 && abs(pxGiant.y - sp.y) < 0.5)
                stars = max(stars, sc);
            else
                stars = max(stars, SpikeLight(pxGiant, sp, GIANT_SPIKE, sc));
        }
    }

    // ── Composite: stars add their light on top of the nebula ─────────────────
    vec3  color   = nebula + stars;

    fragColor = vec4(clamp(color, 0.0, 1.0), 1.0);
}`;

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const qs  = (s, r) => (r || document).querySelector(s);
  const qsa = (s, r) => Array.from((r || document).querySelectorAll(s));
  const snap = (v, s) => Math.round(v / (s || WAVE_SNAP_PX)) * (s || WAVE_SNAP_PX);
  const mql  = window.matchMedia(MQ_MOBILE);

  // ─── Star shader init ─────────────────────────────────────────────────────────
  let rafShader = null;

  function initStarShader() {
    const canvas = document.getElementById('ruleShaderCanvas');
    if (!canvas) return;

    const gl = canvas.getContext('webgl2');
    if (!gl) { canvas.style.background = '#00000a'; return; } // graceful fallback

    function compile(type, src) {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.error('[rulehouse shader]', gl.getShaderInfoLog(s));
        gl.deleteShader(s);
        return null;
      }
      return s;
    }

    const vert = compile(gl.VERTEX_SHADER,   VERT_SRC);
    const frag = compile(gl.FRAGMENT_SHADER, FRAG_SRC);
    if (!vert || !frag) return;

    const prog = gl.createProgram();
    gl.attachShader(prog, vert);
    gl.attachShader(prog, frag);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error('[rulehouse shader link]', gl.getProgramInfoLog(prog));
      return;
    }

    gl.useProgram(prog);

    // Full-screen triangle pair
    const posLoc = gl.getAttribLocation(prog, 'a_position');
    const vao    = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER,
      new Float32Array([-1,-1,  1,-1,  -1,1,  -1,1,  1,-1,  1,1]),
      gl.STATIC_DRAW);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const timeLoc = gl.getUniformLocation(prog, 'u_time');

    function resize() {
      const scale = window.innerWidth < SCALE_BREAKPT_X1 ? SCALE_X1
                  : window.innerWidth < SCALE_BREAKPT    ? SCALE_MOBILE
                  : SCALE_DESKTOP;
      const w = Math.max(1, Math.floor((canvas.clientWidth  || window.innerWidth)  / scale));
      const h = Math.max(1, Math.floor((canvas.clientHeight || window.innerHeight) / scale));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width  = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    }

    const t0 = performance.now();
    function tick() {
      resize();
      gl.useProgram(prog);
      gl.bindVertexArray(vao);
      gl.uniform1f(timeLoc, (performance.now() - t0) / 1000);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      rafShader = requestAnimationFrame(tick);
    }
    rafShader = requestAnimationFrame(tick);

    window.addEventListener('resize', resize);
  }

  function pauseShader()  { if (rafShader) { cancelAnimationFrame(rafShader); rafShader = null; } }
  function resumeShader() { if (!rafShader) initStarShader(); }  // re-init after pause is harmless

  // ─── Layout positioning ───────────────────────────────────────────────────────
  function placeRow() {
    const row  = qs('#linksButtons');
    const logo = qs('#rulehouseLogo');
    if (!row || !logo) return;

    const offset = mql.matches ? MOBILE_ROW_OFFSET : BUTTON_ROW_OFFSET;
    const parent = row.offsetParent || document.body;
    const lr = logo.getBoundingClientRect();
    const pr = parent.getBoundingClientRect();

    row.style.top        = Math.round(lr.top + lr.height / 2 - pr.top + offset) + 'px';
    row.style.visibility = 'visible';
  }

  // Returns the pixel-art display scale matching the current breakpoint tier.
  function previewScale() {
    return window.innerWidth < SCALE_BREAKPT_X1 ? SCALE_X1
         : window.innerWidth < SCALE_BREAKPT    ? SCALE_MOBILE
         : SCALE_DESKTOP;
  }

  function placePreview() {
    const row  = qs('#linksButtons');
    const prev = qs('#linksPreview');
    if (!row || !prev) return;

    // Size to an exact pixel-art multiple; only once the image has actually loaded.
    if (prev.naturalWidth) {
      const s = previewScale();
      prev.style.width  = (prev.naturalWidth  * s) + 'px';
      prev.style.height = (prev.naturalHeight * s) + 'px';
    }

    const parent = prev.offsetParent || document.body;
    const pr = parent.getBoundingClientRect();
    const rr = row.getBoundingClientRect();

    const prevH  = prev.offsetHeight || 96;
    const rowTop = rr.top - pr.top;
    prev.style.top = Math.round(rowTop - PREVIEW_ABOVE_ROW - prevH / 2) + 'px';
  }

  // ─── Preview swap ─────────────────────────────────────────────────────────────
  // Main buttons (.links-button-link) always have data-preview.
  // Overflow links (.links-overflow-link) have data-preview only for platforms with
  // a *_word.png; hovering a no-word overflow link hides the preview instead.
  function setupPreview() {
    const container = qs('#linksButtons');
    const prev      = qs('#linksPreview');
    if (!container || !prev) return;

    // Size + position after load, then reveal — ensures image is never shown before
    // its pixel-art scaled dimensions are applied.
    prev.addEventListener('load', () => {
      placePreview();
      prev.classList.add('is-visible');
    });

    function show(a) {
      const src = a.dataset.preview;
      if (!src) return;

      // Prefer a rulehouse-specific word image; fall back to the links version
      // if no rulehouse equivalent exists (onerror).
      const rhSrc      = src.replace('images/links/', 'images/rulehouse/');
      const primarySrc = (rhSrc !== src) ? rhSrc : src;

      if (prev.dataset.activeSrc !== primarySrc) {
        prev.onerror = null;
        prev.src               = primarySrc;
        prev.dataset.activeSrc = primarySrc;
        if (primarySrc !== src) {
          prev.onerror = function () {
            prev.onerror = null;
            prev.src = src;          // load event handles sizing + is-visible
          };
        }
      } else {
        prev.classList.add('is-visible');
      }
    }
    function hide() { prev.classList.remove('is-visible'); }

    container.addEventListener('mouseover', e => {
      const mainBtn = e.target.closest('.links-button-link');
      if (mainBtn) { show(mainBtn); return; }
      const overflowBtn = e.target.closest('.links-overflow-link');
      if (overflowBtn) {
        if (overflowBtn.dataset.preview) show(overflowBtn);
        else hide(); // no word art — clear any lingering preview
      }
    }, true);
    container.addEventListener('mouseleave', hide);
    container.addEventListener('focusin', e => {
      const a = e.target.closest('.links-button-link, .links-overflow-link');
      if (!a) return;
      if (a.dataset.preview) show(a); else hide();
    });
    container.addEventListener('focusout', hide);
  }

  // ─── Desktop icon wrap clip ───────────────────────────────────────────────────
  // Template limits main buttons to 4; this additionally hides any that wrap on
  // narrow desktops, and restores them when switching to mobile.
  function clipWrappedButtons() {
    const items = qsa('.links-button');
    if (mql.matches) {
      items.forEach(el => { el.hidden = false; }); // restore on mobile
      return;
    }
    if (items.length < 2) return;
    items.forEach(el => { el.hidden = false; }); // reset before measuring
    const refTop = items[0].offsetTop;
    for (let i = 1; i < items.length; i++) {
      if (items[i].offsetTop > refTop + 4) {
        for (let j = i; j < items.length; j++) items[j].hidden = true;
        return;
      }
    }
  }

  // ─── Wave animation ───────────────────────────────────────────────────────────
  let rafD = null, rafM = null;

  function startDesktopWave() {
    const items = qsa('.links-button');
    if (!items.length) return;
    const t0 = performance.now();
    function tick(now) {
      const t = now - t0;
      for (let i = 0; i < items.length; i++) {
        items[i].style.setProperty('--wave-y', snap(Math.sin(t * WAVE_SPEED + i * WAVE_PHASE_STEP) * WAVE_AMP_PX) + 'px');
        items[i].style.setProperty('--wave-x', '0px');
      }
      rafD = requestAnimationFrame(tick);
    }
    rafD = requestAnimationFrame(tick);
  }
  function stopDesktopWave() { if (rafD) { cancelAnimationFrame(rafD); rafD = null; } }

  function startMobileWave() {
    const items = qsa('.links-mobile-item');
    if (!items.length) return;
    const t0 = performance.now();
    function tick(now) {
      const t = now - t0;
      // Half amplitude at the x1 tier (<500px) so buttons sway proportionally less
      const amp = window.innerWidth < 500 ? MOBILE_WAVE_X_AMP / 2 : MOBILE_WAVE_X_AMP;
      for (let i = 0; i < items.length; i++) {
        items[i].style.setProperty('--wave-x', snap(Math.sin(t * MOBILE_WAVE_X_SPEED + i * MOBILE_WAVE_X_PHASE) * amp, MOBILE_WAVE_X_SNAP) + 'px');
        items[i].style.setProperty('--wave-y', '0px');
      }
      rafM = requestAnimationFrame(tick);
    }
    rafM = requestAnimationFrame(tick);
  }
  function stopMobileWave() { if (rafM) { cancelAnimationFrame(rafM); rafM = null; } }

  function applyMode() {
    if (mql.matches) {
      stopDesktopWave();
      qsa('.links-button').forEach(el => el.style.setProperty('--wave-y', '0px'));
      startMobileWave();
    } else {
      stopMobileWave();
      qsa('.links-mobile-item').forEach(el => el.style.setProperty('--wave-x', '0px'));
      startDesktopWave();
    }
    placeRow();
    placePreview();
    clipWrappedButtons();
  }

  // ─── Mobile label scaling ─────────────────────────────────────────────────────
  function scaleMobileLabels() {
    const s = previewScale();   // reuse the same 3-tier scale (1 / 2 / 4)
    qsa('.links-mobile-label').forEach(img => {
      if (!img.naturalWidth) return;
      img.style.width  = (img.naturalWidth  * s) + 'px';
      img.style.height = (img.naturalHeight * s) + 'px';
    });
  }
  qsa('.links-mobile-label').forEach(img => {
    if (img.complete && img.naturalWidth) return;
    img.addEventListener('load', scaleMobileLabels, { once: true });
  });

  // ─── Rulehouse asset swaps ────────────────────────────────────────────────────
  // For any image that currently loads from images/links/, probe for a matching
  // file under images/rulehouse/.  Swap src on success; leave original on 404.
  // Called once at boot — eager images have .src resolved to an absolute URL.

  function swapMobileLabelsToRh() {
    qsa('.links-mobile-label').forEach(img => {
      const orig = img.src;
      if (!orig.includes('images/links/')) return;
      const rh = orig.replace('images/links/', 'images/rulehouse/');
      const probe = new Image();
      probe.onload = () => {
        img.src = rh;
        // Re-scale after swap — rulehouse art may have different native dims.
        img.addEventListener('load', scaleMobileLabels, { once: true });
      };
      probe.src = rh;
    });
  }

  function swapIconsToRh() {
    // Desktop button icons (.links-button-link img) and mobile icons (.links-mobile-icon).
    // Icons are CSS-sized so no JS rescaling needed after the swap.
    qsa('.links-button-link img, .links-mobile-icon').forEach(img => {
      const orig = img.src;
      if (!orig.includes('images/links/')) return;
      const rh = orig.replace('images/links/', 'images/rulehouse/');
      const probe = new Image();
      probe.onload = () => { img.src = rh; };
      probe.src = rh;
    });
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────────
  function boot() {
    initStarShader();
    setupPreview();
    swapMobileLabelsToRh();
    swapIconsToRh();
    scaleMobileLabels();
    applyMode();

    window.addEventListener('resize', () => { placeRow(); placePreview(); clipWrappedButtons(); scaleMobileLabels(); });

    if (mql.addEventListener) mql.addEventListener('change', applyMode);
    else                       mql.addListener(applyMode); // Safari <14

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        pauseShader();
        stopDesktopWave();
        stopMobileWave();
      } else {
        initStarShader();
        applyMode();
      }
    });
  }

  const logo = document.getElementById('rulehouseLogo');
  if (!logo) return;
  if (logo.complete && logo.naturalWidth > 0) boot();
  else logo.addEventListener('load', boot, { once: true });

})();
